import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';

import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Toast } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { NavigationContextService } from '../../../core/services/navigation-context.service';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { escapeHtml } from '../../../core/validators/no-special-chars.validator';
import { decodeSubDomain } from '../../models/document-asset-labels';
import {
  DocumentAssetStatus,
  DocumentAssetSummary,
} from '../../models/document-asset.model';
import { DocumentTemplateV2Service } from '../../services/document-template-v2.service';
import { ColumnConfig } from '../dashboard/table-config';
import {
  DocumentAssetRegisteredEvent,
  DocumentAssetUploadWizardComponent,
} from '../document-asset-upload-wizard/document-asset-upload-wizard.component';

interface TemplateRow {
  assetId: number;
  id: number;
  businessKey: string;
  name: string;
  domainType: string;
  subDomainType: string;
  versionLabel: string;
  status: DocumentAssetStatus | string;
  createdDate: string;
  lastModified: string;
  originalFileName?: string;
  raw: DocumentAssetSummary;
}

@Component({
  selector: 'app-document-template-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RegistryTableComponent,
    Toast,
    BreadcrumbModule,
    ButtonModule,
    ConfirmDialogModule,
    ToggleSwitchModule,
    DocumentAssetUploadWizardComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './document-template-list.component.html',
  styleUrls: ['./document-template-list.component.scss'],
})
export class DocumentTemplateListComponent implements OnInit {
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly templateService = inject(DocumentTemplateV2Service);
  private readonly navigationContext = inject(NavigationContextService);
  private readonly titleService = inject(Title);

  readonly templatesData = signal<TemplateRow[]>([]);
  readonly isLoading = signal<boolean>(false);
  readonly breadcrumbItems = signal<MenuItem[]>([]);
  readonly wizardVisible = signal<boolean>(false);
  readonly wizardMode = signal<'create' | 'edit'>('create');
  readonly editingAsset = signal<DocumentAssetSummary | null>(null);
  readonly showInactive = signal<boolean>(false);
  readonly emptyMessage = signal<string>(
    'Nessun template documentale registrato. Carica il tuo primo template DOCX.',
  );

  readonly columns: ColumnConfig[] = [
    { field: 'name', title: 'Nome Template', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca nome' },
    { field: 'subDomainType', title: 'Categoria', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca categoria' },
    { field: 'versionLabel', title: 'Versione', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca versione' },
    {
      field: 'status',
      title: 'Stato',
      type: 'tag',
      sortable: true,
      filterType: 'select',
      filterPlaceholder: 'Filtra stato',
      filterOptions: [
        { label: 'Bozza', value: 'DRAFT' },
        { label: 'Attivo', value: 'ACTIVE' },
        { label: 'Ritirato', value: 'RETIRED' },
      ],
    },
    { field: 'lastModified', title: 'Ultima Modifica', type: 'text', sortable: true, filterType: 'date' },
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];

  ngOnInit(): void {
    this.titleService.setTitle('Template Documentali - PalEasy');
    this.breadcrumbItems.set([{ label: 'Template Documentali' }]);
    this.loadTemplates();
  }

  loadTemplates(): void {
    this.isLoading.set(true);
    const domainType = this.currentDomainType();
    this.templateService
      .list({ domainType, includeInactive: this.showInactive() })
      .subscribe({
        next: (response) => {
          const items = response?.data ?? [];
          this.templatesData.set(items.map((item) => this.toRow(item)));
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Error loading V2 templates:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare i template documentali',
          });
          this.templatesData.set([]);
          this.isLoading.set(false);
        },
      });
  }

  createNewTemplate(): void {
    this.wizardMode.set('create');
    this.editingAsset.set(null);
    this.wizardVisible.set(true);
  }

  onToggleShowInactive(value: boolean): void {
    this.showInactive.set(value);
    this.loadTemplates();
  }

  onEditClick(row: TemplateRow): void {
    this.wizardMode.set('edit');
    this.editingAsset.set(row.raw);
    this.wizardVisible.set(true);
  }

  onDownloadClick(row: TemplateRow): void {
    this.templateService.download(row.assetId).subscribe({
      next: (blob) => this.triggerDownload(blob, row.originalFileName || `${row.businessKey}.docx`),
      error: (err) => {
        console.error('Error downloading template:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile scaricare il template',
        });
      },
    });
  }

  onWizardRegistered(event: DocumentAssetRegisteredEvent): void {
    this.loadTemplates();
    this.messageService.add({
      severity: 'success',
      summary: 'Template registrato',
      detail: `${event.businessKey} v${event.version} \u2014 stato ${event.status.toLowerCase()}.`,
      life: 4000,
    });
  }

  onDeleteClick(row: TemplateRow): void {
    this.confirmationService.confirm({
      header: 'Conferma Eliminazione',
      message: `Sei sicuro di voler ritirare il template <strong>${escapeHtml(row.name || row.businessKey)}</strong>? L'operazione è reversibile creando una nuova versione.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, ritira',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => this.performSoftDelete(row),
    });
  }

  onClearFilters(): void {
    this.showInactive.set(false);
    this.loadTemplates();
  }

  onExport(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Esportazione',
      detail: 'La funzione di esportazione sarà disponibile a breve.',
      life: 3000,
    });
  }

  private performSoftDelete(row: TemplateRow): void {
    const templateRef = row.raw.assetId;
    this.templateService.softDelete(templateRef).subscribe({
      next: (response) => {
        if (response?.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Template ritirato',
            detail: `Il template "${row.name || row.businessKey}" è stato ritirato correttamente.`,
            life: 3000,
          });
          this.loadTemplates();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: response?.message ?? 'Impossibile ritirare il template',
          });
        }
      },
      error: (err) => {
        console.error('Error soft-deleting template:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile ritirare il template',
        });
      },
    });
  }

  private toRow(item: DocumentAssetSummary): TemplateRow {
    return {
      assetId: item.assetId,
      id: item.assetId,
      businessKey: item.businessKey,
      name: item.name ?? '',
      domainType: item.domainType ?? '',
      subDomainType: decodeSubDomain(item.subDomainType),
      versionLabel: `v${item.version}`,
      status: item.status ?? 'DRAFT',
      createdDate: this.formatDate(item.createdAt),
      lastModified: this.formatDate(item.updatedAt ?? item.createdAt),
      originalFileName: item.originalFileName,
      raw: item,
    };
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private formatDate(value?: string): string {
    if (!value) return '-';
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('it-IT');
  }

  currentDomainType(): 'SUPPLIERS' | 'PROFESSIONALS' {
    return this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
  }

  private domainPrefix(): string {
    return this.navigationContext.getDomainPrefix();
  }
}
